/**
 * Lightweight accessibility settings.
 * - Persists to localStorage so preferences survive page reloads.
 * - Applies CSS classes / a data attribute to the <html> element so the
 *   stylesheet can react globally.
 */
import { useEffect, useState, useCallback } from 'react';

const KEY = 'tfi_a11y_v1';

const DEFAULTS = {
  fontScale: 1, // 0.9, 1, 1.15, 1.3
  highContrast: false,
  dyslexiaFont: false,
  reduceMotion: false,
  underlineLinks: false,
};

function readStored() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
    return null;
  } catch {
    return null;
  }
}

function applyToDocument(settings) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--a11y-font-scale', String(settings.fontScale || 1));
  root.classList.toggle('a11y-high-contrast', !!settings.highContrast);
  root.classList.toggle('a11y-dyslexia', !!settings.dyslexiaFont);
  root.classList.toggle('a11y-reduce-motion', !!settings.reduceMotion);
  root.classList.toggle('a11y-underline-links', !!settings.underlineLinks);
}

export function useAccessibility() {
  const [settings, setSettings] = useState(() => ({
    ...DEFAULTS,
    ...(readStored() || {}),
  }));

  useEffect(() => {
    applyToDocument(settings);
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      // ignore quota / disabled storage
    }
  }, [settings]);

  const update = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setSettings(DEFAULTS), []);

  return { settings, update, reset };
}
